import type { UpsertGuessInput, UpsertTastingInput } from '@cercle/shared';

/**
 * Une écriture en attente de réseau.
 *
 * On ne met en file QUE les écritures idempotentes identifiées par une clé métier
 * (séance + rhum + membre) : rejouer deux fois la même entrée doit être sans effet. C'est
 * pour cela que l'API expose `PUT /tastings` et non `POST` — sans cela, une reconnexion
 * hasardeuse créerait des doublons.
 */
export type QueuedOperation =
  | { kind: 'tasting'; payload: UpsertTastingInput }
  | { kind: 'guess'; payload: UpsertGuessInput };

export interface QueueEntry {
  /** Clé métier : deux saisies successives du même rhum se remplacent au lieu de s'empiler. */
  id: string;
  operation: QueuedOperation;
  /** Horodatage de la saisie sur l'appareil — arbitre le last-write-wins côté serveur. */
  clientUpdatedAt: string;
  /** Nombre d'échecs réseau successifs, pour espacer les tentatives. */
  attempts: number;
  /** Renseigné quand le serveur a définitivement refusé l'entrée. */
  lastError?: string;
  queuedAt: string;
}

export type SyncState = 'idle' | 'syncing' | 'offline' | 'error';

export interface SyncStatus {
  state: SyncState;
  /** Nombre d'entrées en attente — le « 2 en attente » de l'en-tête des maquettes. */
  pending: number;
  /** Entrées abandonnées parce que le serveur détenait une version plus récente. */
  discarded: number;
  lastSyncedAt: string | null;
}

/** Clé métier d'une opération : c'est elle qui rend la file déduplicante. */
export function operationKey(operation: QueuedOperation): string {
  const { sessionId, rumId } = operation.payload;
  return keyFor(operation.kind, sessionId, rumId);
}

export function keyFor(kind: QueuedOperation['kind'], sessionId: string, rumId: string): string {
  return `${kind}:${sessionId}:${rumId}`;
}
